import { Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { ToastService } from '@ceslicensing/toast-notification';
import { takeUntil } from 'rxjs/operators';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { GlobalErrorHandlerService } from '@app/core/services';
import { CustomValidationService } from '@app/core/validators';
import { UserService } from '@app/core/http-services/user.service';
import { environment } from '@env/environment';

export interface Load {
  loading: boolean;
  submitted: boolean;
  gotResponse: 'success' | 'error' | null;
  toastMessage: string;
  errorMessage: string;
}
@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],
})
export class ChangePasswordComponent {
  destroy$: Subject<boolean> = new Subject<boolean>();
  loader: boolean = false;
  pwdForm: FormGroup;
  confirmPasswordIcon: string = 'icon-password';
  loadingConfig: { change: Load } = {
    change: {
      loading: false,
      submitted: false,
      gotResponse: null,
      toastMessage: '',
      errorMessage: '',
    },
  };
  authMasterConnection: string = environment.authMasterDb;
  @Input() userDetails: any;
  constructor(
    private userService: UserService,
    private toasterService: ToastService,
    private formBuilder: FormBuilder,
    public dialog: DialogRef,
    private errorService: GlobalErrorHandlerService,
    private customValidator: CustomValidationService
  ) {}

  ngOnInit(): void {
    this.loadResetPassForm();
  }
  /* Angular destroy fn */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  /* Reset Password Form getter */
  get pwdFormCtrl() {
    return this.pwdForm.controls;
  }

  /* Custom validation getter */
  get customV() {
    return this.customValidator;
  }

  loadResetPassForm() {
    this.pwdForm = this.formBuilder.group(
      {
        newPassword: [
          '',
          Validators.compose([
            Validators.required,
            this.customValidator.passwordLength(),
            this.customValidator.passwordUpperCase(),
            this.customValidator.passwordLowerCase(),
            this.customValidator.passwordNumber(),
            this.customValidator.passwordSpecialCharacter(),
          ]),
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validator: [this.customValidator.passwordMatch('newPassword', 'confirmPassword')],
      }
    );
  }

  /**
   * close modal
   */
  dismiss() {
    this.close(false);
  }
  /**
   * Close modal
   * @param {any} isSuccess - on close boolean value pass for further action
   */
  close(isSuccess) {
    this.dialog.close({ isSuccess });
  }

  /**
   * Submit change passowrd form
   */
  onSubmit() {
    this.inputResetMessageMessage();
    if (this.pwdForm.valid) {
      this.loadingConfig.change.submitted = true;
      this.loadingConfig.change.loading = true;

      let params: any = {
        connection: this.authMasterConnection,
        password: this.pwdForm.value.newPassword,
      };
      this.userService
        .updateUser(this.userDetails.user_id, params)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          () => {
            this.close(true);
            this.loadingConfig.change.loading = false;
            this.loadingConfig.change.submitted = false;
            this.toasterService.success('Your password has been changed successfully!');
          },
          (err) => {
            this.loadingConfig.change.loading = false;
            this.loadingConfig.change.submitted = false;
            if (err?.error?.message) {
              this.toasterService.error(err.error.message);
            }
            this.errorService.apiErrorHandle(err);
          }
        );
    }
  }

  /**
   * On password click toggle password view icon
   */
  passwordChange() {
    this.loadingConfig.change.errorMessage = '';
    this.confirmPasswordIcon = this.pwdFormCtrl.confirmPassword.valid
      ? 'icon-password-tick'
      : 'icon-password';
    this.inputResetMessageMessage();
  }

  /**
   * Validation messages
   */
  inputResetMessageMessage() {
    if (this.pwdFormCtrl.newPassword.errors?.required) {
      this.loadingConfig.change.errorMessage = 'Please enter a new password first';
    } else if (this.pwdFormCtrl.confirmPassword.errors?.required) {
      this.loadingConfig.change.errorMessage = 'Please enter confirmation password';
    } else if (this.pwdForm.errors?.notMatch) {
      this.loadingConfig.change.errorMessage = 'Passwords & repeat password are not the same';
    } else {
      this.loadingConfig.change.errorMessage = '';
    }
  }
}
